// ExtractionContext.jsx
import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    createExtraction,
    moveToStep3,
    resetExtractionForm,
    setExtErrors,
    setStepVal,
} from '../../store/extractions/action';
import { toast } from 'react-toastify';
import { createExtVersion } from '../../store/extVersions/actions';
import { useLocation, useNavigate } from 'react-router-dom';
import { cleanArray } from '../../commonUtils/string.helper';

const ExtractionContext = createContext();

export const useExtractionContext = () => {
    const context = useContext(ExtractionContext);
    if (!context) {
        throw new Error('useExtractionContext must be used within an ExtractionsProvider');
    }
    return context;
};

const STEPS = [
    { id: 1, label: 'Project Details' },
    { id: 2, label: 'Data Strategy' },
    { id: 3, label: 'Column Selection' },
    { id: 4, label: 'Extractions' },
    // { id: 5, label: 'Multichannel Campaign' },
];

const STEP_SUB_STEPS = {
    1: [],
    2: ['Data Groups', 'Data Categories', 'Exclusions'],
    3: [],
};

export const ExtractionsProvider = ({ children }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [currentSubStep, setCurrentSubStep] = useState(0);

    const { stepVal, extractionData, extractionViewOnly } = useSelector(
        (state) => state.Extractions
    );
    const { extVersion } = useSelector((state) => state.ExtVersions);
    const { ExtDataGroupsList } = useSelector((state) => state.ExtDataGroups);
    const { ExtDataCategoriesList } = useSelector((state) => state.ExtDataCategories);

    // -----------------------------------------------------------------------
    // Sub-step helpers
    // -----------------------------------------------------------------------
    const getSubSteps = useCallback((step) => STEP_SUB_STEPS[step] || [], []);
    const subStepsForStep = useMemo(() => getSubSteps(stepVal), [stepVal, getSubSteps]);
    const subStepLabel = useMemo(
        () => subStepsForStep[currentSubStep],
        [subStepsForStep, currentSubStep]
    );

    const canGoNext = useMemo(() => {
        if (subStepsForStep.length > 0) {
            return currentSubStep < subStepsForStep.length - 1 || stepVal < STEPS.length;
        }
        return stepVal < STEPS.length;
    }, [subStepsForStep, currentSubStep, stepVal]);

    const canGoPrevious = useMemo(
        () => extractionViewOnly && (stepVal > 1 || currentSubStep > 0),
        [stepVal, currentSubStep, extractionViewOnly]
    );

    const isLastStep = useMemo(
        () =>
            stepVal === STEPS.length &&
            (subStepsForStep.length === 0 || currentSubStep === subStepsForStep.length - 1),
        [stepVal, subStepsForStep, currentSubStep]
    );

    const maxAccessibleStep = useMemo(() => {
        const workflowStep = Number(extractionData?.workflow_step);
        if (Number.isFinite(workflowStep) && workflowStep >= 0) {
            return Math.min(STEPS.length, workflowStep + 1);
        }
        return stepVal;
    }, [extractionData?.workflow_step, stepVal]);

    const isCreatorEditStep4Locked = useMemo(
        () => !extractionViewOnly && stepVal === 4,
        [extractionViewOnly, stepVal]
    );

    const canNavigateToStep = useCallback(
        (stepId) => {
            if (stepId < 1 || stepId > STEPS.length) return false;

            // View-only: must be first — before any edit-mode restriction.
            // A viewer with a completed workflow can freely access all steps.
            if (extractionViewOnly) return true;

            // --- Edit mode rules below ---
            if (!extractionData?.workflow_step || !extractionData?.key) return false;

            // If on step 5, step 4 is navigable but 1, 2, 3 are disabled.
            if (stepVal === 5 && stepId < 4) return false;

            // Step 5 is only reachable if a version has been selected.
            if (stepId === 5 && !extractionData?.selected_version) return false;

            if (stepId > maxAccessibleStep) return false;

            if (isCreatorEditStep4Locked && stepId !== 4) {
                // On step 4, step 5 is reachable if a version is selected.
                if (stepId === 5 && extractionData?.selected_version) return true;
                return false;
            }

            return true;
        },
        [
            extractionViewOnly,
            extractionData?.workflow_step,
            extractionData?.key,
            extractionData?.selected_version,
            maxAccessibleStep,
            isCreatorEditStep4Locked,
            stepVal,
        ]
    );

    // -----------------------------------------------------------------------
    // Validation + create extraction (Step 1)
    // -----------------------------------------------------------------------
    const validateAndCreateExtraction = useCallback(() => {
        const errors = {};
        const hasDropdownValues =
            extractionData.project && extractionData.masterProject;
        const hasTempValues =
            extractionData.tempProject && extractionData.tempMasterProject;

        if (!hasDropdownValues && !hasTempValues) {
            if (extractionData.addTemporaryValues) {
                if (!extractionData.tempProject)
                    errors.tempProject = 'Please enter a temporary Project name.';
                if (!extractionData.tempMasterProject)
                    errors.tempMasterProject = 'Please enter a temporary Master Project name.';
            } else {
                if (!extractionData.project)
                    errors.project = 'Please select a Project.';
                if (!extractionData.masterProject)
                    errors.masterProject = 'Please select a Master Project.';
            }
        }

        const approxPrice = String(extractionData.approxStartingPrice ?? '').trim();
        if (approxPrice === '') {
            errors.approxStartingPrice = 'Please enter an Approximate Starting Price.';
        } else if (!/^\d+(\.\d+)?$/.test(approxPrice)) {
            errors.approxStartingPrice = 'Approximate Starting Price must be numeric only.';
        }

        const pincode = String(extractionData.pincode || '').trim();
        if (!pincode) {
            errors.pincode = 'Please enter a Pincode.';
        } else if (!/^\d{6}$/.test(pincode)) {
            errors.pincode = 'Pincode must be exactly 6 digits.';
        }

        if (Object.keys(errors).length > 0) {
            dispatch(setExtErrors(errors));
            return;
        }

        dispatch(setExtErrors({}));
        dispatch(createExtraction({ extractionData, navigate }));
    }, [extractionData, dispatch, navigate]);

    // -----------------------------------------------------------------------
    // handleNext
    // -----------------------------------------------------------------------
    const handleNext = useCallback(() => {
        if (extractionViewOnly) {
            if (
                subStepsForStep.length > 0 &&
                currentSubStep < subStepsForStep.length - 1
            ) {
                setCurrentSubStep((c) => c + 1);
                return;
            }
            if (stepVal < STEPS.length) {
                dispatch(setStepVal(stepVal + 1));
                setCurrentSubStep(0);
            }
            return;
        }

        // Handle sub-steps
        if (subStepsForStep.length > 0 && currentSubStep < subStepsForStep.length - 1) {
            if (currentSubStep === 0 && ExtDataGroupsList.data.length === 0) {
                toast.error('Please add at least one Data Group to proceed.');
                return;
            }
            if (currentSubStep === 1 && ExtDataCategoriesList.data.length === 0) {
                toast.error('Please add at least one Data Category to proceed.');
                return;
            }
            setCurrentSubStep((c) => c + 1);
            return;
        }

        // Step 1 — validate & create extraction
        if (stepVal === 1 && !extractionData.id) {
            validateAndCreateExtraction();
            return;
        }

        // Step 2 — move to step 3
        if (stepVal === 2 && currentSubStep === subStepsForStep.length - 1) {
            if (
                ExtDataGroupsList.data.length === 0 ||
                ExtDataCategoriesList.data.length === 0
            ) {
                toast.error(
                    'Please ensure at least one Data Group and one Data Category are added to proceed.'
                );
                return;
            }
            if (extractionData.workflow_step >= 2) {
                dispatch(setStepVal(3));
            } else {
                dispatch(moveToStep3(extractionData.id));
            }
            return;
        }

        // Step 3 — create version (saga will start polling automatically)
        if (stepVal === 3) {
            if (!extVersion.selected_columns || extVersion.selected_columns.length === 0) {
                toast.error('Please select at least one column to proceed.');
                return;
            }
            const { loading: _loading, ...versionPayload } = extVersion;
            dispatch(
                createExtVersion({
                    formData: {
                        selected_columns: cleanArray(versionPayload.selected_columns),
                        custom_columns: cleanArray(versionPayload.custom_columns) || [],
                        is_default: versionPayload.is_default,
                    },
                })
            );
            return;
        }

        // All other steps — move forward
        if (stepVal < STEPS.length) {
            dispatch(setStepVal(stepVal + 1));
            setCurrentSubStep(0);
        }
    }, [
        extractionViewOnly,
        subStepsForStep,
        currentSubStep,
        ExtDataGroupsList,
        ExtDataCategoriesList,
        stepVal,
        extractionData,
        dispatch,
        extVersion,
        validateAndCreateExtraction,
    ]);

    // -----------------------------------------------------------------------
    // handlePrevious
    // -----------------------------------------------------------------------
    const handlePrevious = useCallback(() => {
        if (isCreatorEditStep4Locked) return;

        if (extractionViewOnly) {
            if (currentSubStep > 0) {
                setCurrentSubStep((c) => c - 1);
            } else if (stepVal > 1) {
                const prevStep = stepVal - 1;
                const prevStepSubSteps = getSubSteps(prevStep);
                dispatch(setStepVal(prevStep));
                setCurrentSubStep(prevStepSubSteps.length > 0 ? prevStepSubSteps.length - 1 : 0);
            }
            return;
        }

        if (extractionData?.id && !extractionViewOnly) return;

        if (currentSubStep > 0) {
            setCurrentSubStep((c) => c - 1);
        } else if (stepVal > 1) {
            const prevStep = stepVal - 1;
            const prevStepSubSteps = getSubSteps(prevStep);
            dispatch(setStepVal(prevStep));
            setCurrentSubStep(prevStepSubSteps.length > 0 ? prevStepSubSteps.length - 1 : 0);
        }
    }, [
        isCreatorEditStep4Locked,
        extractionViewOnly,
        currentSubStep,
        stepVal,
        dispatch,
        getSubSteps,
        extractionData,
    ]);

    // -----------------------------------------------------------------------
    // handleNavigateToStep
    // -----------------------------------------------------------------------
    const handleNavigateToStep = useCallback(
        (stepId) => {
            if (!canNavigateToStep(stepId)) return;

            dispatch(setStepVal(stepId));
            setCurrentSubStep(0);

            // From output mappings route, step navigation should take users back to the wizard route.
            if (location.pathname.includes('/extraction/view')) {
                navigate(`/extraction/${extractionData?.key}`);
            }
        },
        [canNavigateToStep, dispatch, extractionData?.key, navigate, location.pathname]
    );

    // -----------------------------------------------------------------------
    // handleReset
    // -----------------------------------------------------------------------
    const handleReset = useCallback(() => {
        dispatch(resetExtractionForm());
        setCurrentSubStep(0);
    }, [dispatch]);

    // -----------------------------------------------------------------------
    // getContentDescription
    // -----------------------------------------------------------------------
    const getContentDescription = useCallback(() => {
        const descriptions = {
            1: 'Please enter the Project Details Here',
            2: `Configure your data settings${subStepLabel ? ` - ${subStepLabel}` : ''}`,
            3: 'Please select the columns you require for this extraction',
        };
        return descriptions[stepVal] || '';
    }, [stepVal, subStepLabel]);

    // -----------------------------------------------------------------------
    // handleExtract (placeholder)
    // -----------------------------------------------------------------------
    const handleExtract = useCallback(() => {
        extVersion?.selected_columns.join(', ');
    }, [extVersion]);

    // -----------------------------------------------------------------------
    // Context value
    // -----------------------------------------------------------------------
    const value = useMemo(
        () => ({
            steps: STEPS,
            stepSubSteps: STEP_SUB_STEPS,
            stepVal,
            extractionViewOnly,
            extractionData,
            currentSubStep,
            setCurrentSubStep,
            subStepsForStep,
            subStepLabel,
            getSubSteps,
            getContentDescription,
            canGoNext,
            canGoPrevious,
            isLastStep,
            canNavigateToStep,
            handleNext,
            handlePrevious,
            handleReset,
            handleExtract,
            handleNavigateToStep,
        }),
        [
            stepVal,
            extractionViewOnly,
            extractionData,
            currentSubStep,
            subStepsForStep,
            subStepLabel,
            canGoNext,
            canGoPrevious,
            isLastStep,
            canNavigateToStep,
            getSubSteps,
            getContentDescription,
            handleNext,
            handlePrevious,
            handleReset,
            handleExtract,
            handleNavigateToStep,
        ]
    );

    return (
        <ExtractionContext.Provider value={value}>{children}</ExtractionContext.Provider>
    );
};